import { createSlice } from '@reduxjs/toolkit';
import axios from '../../utils/axios';
import { dispatch } from '../store';
import { rejectWithError } from '../dispatchError';
import { AiFeedbackConfigurationDataState } from '../../@types/aiFeedbackConfiguration';
import { translator } from 'translation/useTranslator';

const route = 'ai-support-configuration';

const initialState: AiFeedbackConfigurationDataState = {
  isLoading: false,
  configuration: {
    aiSupportFeedbackCampaign: {
      id: 0,
      name: '',
      active: false,
      created: '',
      universalLink: ''
    }
  },
  availableCampaigns: []
};

const slice = createSlice({
  name: 'aiFeedbackConfiguration',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    stopLoading(state) {
      state.isLoading = false;
    },

    getConfigSuccess(state, action) {
      state.isLoading = false;
      state.configuration = action.payload.configuration;
    },

    getCampaignsSuccess(state, action) {
      state.isLoading = false;
      state.availableCampaigns = action.payload.campaigns;
    }
  }
});

export default slice.reducer;

export function getAiFeedbackConfiguration() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(route);
      dispatch(slice.actions.getConfigSuccess({ configuration: response.data }));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.aiFeedbackConfigurationFetched() ?? 'AI feedback configuration fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading());
      const defaultErrorMessage =
        translator?.couldNotFetchAiFeedbackConfiguration() ??
        'Could not fetch AI feedback configuration';
      return await rejectWithError({ error, defaultErrorMessage });
    }
  };
}

export function getAvailableCampaigns() {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const response = await axios.get(`${route}/campaigns`);
      dispatch(slice.actions.getCampaignsSuccess({ campaigns: response.data }));
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.aiFeedbackCampaignsFetched() ?? 'AI feedback campaigns fetched'
      });
    } catch (error) {
      dispatch(slice.actions.stopLoading());
      const defaultErrorMessage =
        translator?.couldNotFetchAiFeedbackCampaigns() ?? 'Could not fetch AI feedback campaigns';
      return await rejectWithError({ error, defaultErrorMessage });
    }
  };
}

type UpdateAiFeedbackConfigurationProps = {
  campaignId: number;
};
export function UpdateAiFeedbackConfiguration({ campaignId }: UpdateAiFeedbackConfigurationProps) {
  dispatch(slice.actions.startLoading());
  return async () => {
    try {
      const response = await axios.put(route, { aiSupportFeedbackCampaignId: campaignId });
      return await Promise.resolve({
        result: response,
        defaultSuccessMessage:
          translator?.aiFeedbackConfigurationUpdated() ?? 'AI feedback configuration updated'
      });
    } catch (error: any) {
      dispatch(slice.actions.stopLoading());
      const defaultErrorMessage =
        translator?.couldNotUpdateAiFeedbackConfiguration() ??
        'Could not update AI feedback configuration';
      return await rejectWithError({ error, defaultErrorMessage });
    }
  };
}
